import React from 'react'
import { sanitizeRichHtml } from 'lib/sanitize'
import { TableRenderer, type TableData, normalizeHtmlBreaks } from './TableRenderer'

interface MarkdownRendererProps {
  content: string
  className?: string
}

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  // Helper function to parse markdown table
  const parseMarkdownTable = (lines: string[]): { table: TableData; endIndex: number } | null => {
    if (lines.length < 3) return null

    const firstLine = lines[0].trim()
    const secondLine = lines[1].trim()

    // Check if first line looks like a table header (contains |)
    if (!firstLine.startsWith('|') || !firstLine.endsWith('|')) return null
    if (!secondLine.startsWith('|') || !secondLine.endsWith('|')) return null

    // Check if second line is a separator
    const separatorCells = secondLine.split('|').map(s => s.trim()).filter(s => s.length > 0)
    const isValidSeparator = separatorCells.every(cell => /^:?-+:?$/.test(cell))

    if (!isValidSeparator) return null

    // Helper function to split table row intelligently
    // Handles pipes inside brackets/braces and preserves them
    const splitTableRow = (line: string): string[] => {
      const cells: string[] = []
      let currentCell = ''
      let depth = 0 // Track nesting level of brackets/braces
      let inBracket = false
      let inBrace = false
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        const nextChar = line[i + 1]
        
        if (char === '[') {
          inBracket = true
          depth++
          currentCell += char
        } else if (char === ']') {
          depth = Math.max(0, depth - 1)
          if (depth === 0) inBracket = false
          currentCell += char
        } else if (char === '{') {
          inBrace = true
          depth++
          currentCell += char
        } else if (char === '}') {
          depth = Math.max(0, depth - 1)
          if (depth === 0) inBrace = false
          currentCell += char
        } else if (char === '|' && depth === 0) {
          // This is a cell separator
          cells.push(currentCell.trim())
          currentCell = ''
        } else {
          currentCell += char
        }
      }
      
      // Add the last cell
      if (currentCell.trim()) {
        cells.push(currentCell.trim())
      }
      
      return cells.filter(s => s.length > 0)
    }

    // Parse headers
    const headers = splitTableRow(firstLine)

    const rows: string[][] = []
    let lineIndex = 2

    // Parse rows
    while (lineIndex < lines.length) {
      const line = lines[lineIndex].trim()

      // Stop if line is empty or doesn't start with |
      if (!line || !line.startsWith('|') || !line.endsWith('|')) {
        break
      }

      const cells = splitTableRow(line)

      // Check if number of cells matches headers (allow +/- 1 for flexibility)
      if (Math.abs(cells.length - headers.length) <= 1) {
        // Pad with empty strings if needed
        while (cells.length < headers.length) {
          cells.push('')
        }
        // Truncate if too many
        if (cells.length > headers.length) {
          cells.length = headers.length
        }
        rows.push(cells)
        lineIndex++
      } else {
        // Too different, stop parsing
        break
      }
    }

    // Return table data and the index where the table ends
    if (rows.length > 0) {
      return {
        table: { headers, rows },
        endIndex: lineIndex
      }
    }

    return null
  }

  // Simple markdown-like formatting for AI responses
  const formatContent = (text: string) => {
    // Do not normalize <br> before line splitting — table rows often contain <br> inside cells
    const lines = text.split('\n')
    const elements: React.ReactNode[] = []
    let inCodeBlock = false
    let codeBlockContent: string[] = []
    let listItems: string[] = []
    let inList = false
    let i = 0

    const flushList = () => {
      if (listItems.length > 0) {
        // Check if it's a numbered list by looking at the first item
        const isNumbered = listItems[0].match(/^\d+\.\s/)
        
        if (isNumbered) {
          elements.push(
            <ol key={`list-${elements.length}`} className="list-decimal list-inside space-y-1.5 my-3 pl-4">
              {listItems.map((item, index) => (
                <li key={index} className="text-sm leading-relaxed">
                  {formatInlineText(item.replace(/^\d+\.\s/, ''))}
                </li>
              ))}
            </ol>
          )
        } else {
          elements.push(
            <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1.5 my-3 pl-4">
              {listItems.map((item, index) => (
                <li key={index} className="text-sm leading-relaxed">
                  {formatInlineText(item)}
                </li>
              ))}
            </ul>
          )
        }
        listItems = []
        inList = false
      }
    }

    const flushCodeBlock = () => {
      if (codeBlockContent.length > 0) {
        elements.push(
          <pre key={`code-${elements.length}`} className="bg-muted/50 p-4 rounded-lg overflow-x-auto my-3 border">
            <code className="text-sm font-mono leading-relaxed">
              {codeBlockContent.join('\n')}
            </code>
          </pre>
        )
        codeBlockContent = []
        inCodeBlock = false
      }
    }

    const formatInlineText = (text: string) => {
      let html = normalizeHtmlBreaks(text)
      // Handle bold text **text** or __text__
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/__(.*?)__/g, '<strong>$1</strong>')
      
      // Handle italic text *text* or _text_
      html = html.replace(/(?<!\*)\*(?!\*)([^*]+)\*(?!\*)/g, '<em>$1</em>')
      html = html.replace(/(?<!_)_(?!_)([^_]+)_(?!_)/g, '<em>$1</em>')
      
      // Handle inline code `code`
      html = html.replace(/`(.*?)`/g, '<code class="bg-muted/50 px-1.5 py-0.5 rounded text-xs font-mono border">$1</code>')
      
      // Handle links [text](url)
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')

      html = html.replace(/\n/g, '<br>')
      
      return <span dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }} />
    }

    while (i < lines.length) {
      const line = lines[i]
      const trimmedLine = line.trim()

      // Try to parse table at current position
      if (trimmedLine.startsWith('|') && !inCodeBlock) {
        const tableResult = parseMarkdownTable(lines.slice(i))
        if (tableResult) {
          flushList()
          const tableKey = `table-${elements.length}`
          elements.push(
            <div key={tableKey} className="overflow-x-auto max-w-full">
              <TableRenderer table={tableResult.table} />
            </div>
          )
          i += tableResult.endIndex
          continue
        }
      }

      // Handle code blocks
      if (trimmedLine.startsWith('```')) {
        if (inCodeBlock) {
          flushCodeBlock()
        } else {
          flushList()
          inCodeBlock = true
        }
        i++
        continue
      }

      if (inCodeBlock) {
        codeBlockContent.push(line)
        i++
        continue
      }

      // Handle headers
      if (trimmedLine.startsWith('#')) {
        flushList()
        const level = trimmedLine.match(/^#+/)?.[0].length || 1
        const text = trimmedLine.replace(/^#+\s*/, '')
        const headingTags: Array<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
        const headingTag = headingTags[Math.min(level, 6) - 1]
        const headingClass =
          level === 1
            ? 'text-lg border-b border-border pb-1'
            : level === 2
              ? 'text-base'
              : 'text-sm'
        elements.push(
          React.createElement(
            headingTag,
            {
              key: i,
              className: `font-bold my-3 ${headingClass}`,
            },
            formatInlineText(text)
          )
        )
        i++
        continue
      }

      // Handle list items
      if (trimmedLine.match(/^[-*+]\s/) || trimmedLine.match(/^\d+\.\s/)) {
        if (!inList) {
          flushList()
          inList = true
        }
        // Keep the original format for numbered lists to detect them later
        const item = trimmedLine.replace(/^[-*+]\s/, '')
        listItems.push(item)
        i++
        continue
      }

      // Handle blockquotes
      if (trimmedLine.startsWith('>')) {
        flushList()
        const quoteText = trimmedLine.replace(/^>\s*/, '')
        elements.push(
          <blockquote key={i} className="border-l-4 border-primary/30 pl-4 my-3 italic text-muted-foreground bg-muted/30 py-2 rounded-r">
            <p className="text-sm leading-relaxed">{formatInlineText(quoteText)}</p>
          </blockquote>
        )
        i++
        continue
      }

      // Handle horizontal rules
      if (trimmedLine.match(/^[-*_]{3,}$/)) {
        flushList()
        elements.push(<hr key={i} className="my-4 border-border" />)
        i++
        continue
      }

      // Handle regular paragraphs
      flushList()
      if (trimmedLine) {
        elements.push(
          <p key={i} className="text-sm leading-relaxed my-2">
            {formatInlineText(trimmedLine)}
          </p>
        )
      } else {
        i++
        continue
      }
      i++
    }

    // Flush any remaining content
    flushList()
    flushCodeBlock()

    return elements
  }

  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      {formatContent(content)}
    </div>
  )
}
